<template>
  <div class="min-h-screen flex flex-col items-center justify-center py-8 px-4">
    <div class="w-full max-w-2xl">
      <header class="mb-6">
        <h1 id="form-title" class="text-2xl font-semibold text-koshee-orange uppercase">
          Support Ticket
        </h1>
        <p class="text-sm text-gray-600">
          Complete the fields below to open a new ticket with our support team.
        </p>
      </header>

      <StatusBanner :state="statusState" />

      <div v-if="formSchema" class="space-y-0">
        <FormRenderer
          :schema="formSchema"
          :submitting="isSubmitting"
          :backend-errors="fieldErrors"
          @submit="handleSubmit"
        />

        <div class="flex justify-center items-center my-6">
          <FileUpload
            v-model="attachments"
            :disabled="isSubmitting"
            class="w-full"
          />
        </div>
      </div>

      <div v-else class="text-sm text-slate-500">
        Loading ticket form&hellip;
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import StatusBanner from "./components/StatusBanner.vue";
import FormRenderer from "./components/FormRenderer.vue";
import TurnstileWidget from "./components/TurnstileWidget.vue";
import FileUpload from "./components/FileUpload.vue";
import { useFormSchema } from "./composables/useFormSchema";
import { useTicketSubmit } from "./composables/useTicketSubmit";
import type { AttachmentRef } from "@hubspot-ticket/shared";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const turnstileToken = ref<string | undefined>();
const attachments = ref<AttachmentRef[]>([]);

const { schema: formSchema, status: schemaStatus } = useFormSchema();
const { submit, status: submitStatus, isSubmitting, fieldErrors } = useTicketSubmit();

const statusState = computed(() => {
  if (schemaStatus.value.type === "error") {
    return schemaStatus.value;
  }
  return submitStatus.value;
});

// Clear attachments after successful submission
watch(submitStatus, (newStatus) => {
  if (newStatus.type === "success") {
    attachments.value = [];
  }
});

const handleTurnstileToken = (token: string) => {
  turnstileToken.value = token;
};

const handleTurnstileError = () => {
  turnstileToken.value = undefined;
};

const handleTurnstileExpired = () => {
  turnstileToken.value = undefined;
};

const handleSubmit = async (payload: unknown) => {
  // Add attachments to payload
  // Safely construct the payload with type checking
  if (typeof payload !== "object" || payload === null) {
    return;
  }

  const typedPayload = payload as Record<string, unknown>;
  const turnstileTokenFromForm = typedPayload.turnstileToken as string | undefined;
  delete typedPayload.turnstileToken;

  const ticket = typeof typedPayload.ticket === "object" && typedPayload.ticket !== null
    ? typedPayload.ticket as Record<string, unknown>
    : {};

  const payloadWithAttachments = {
    ...typedPayload,
    ticket: {
      ...ticket,
      attachments: attachments.value.length > 0 ? attachments.value : undefined,
    },
  };

  await submit(payloadWithAttachments, turnstileTokenFromForm);
};
</script>
